<template>
  <!-- A user who may use only Search gets no tab bar and no brand row: a tab strip holding a
       single tab reads as a mistake. They see the plain item search page instead. -->
  <div class="pulse-nav" v-if="userCanSeePulseTabs">
    <!-- Brand row. It sits on the page ground, not on the tab bar: a brand that shares a
         surface with controls inherits their meaning and starts reading as a fourth tab. -->
    <div class="pulse-brandrow">
      <span class="pulse-lockup">
        <!-- The mark is inlined rather than linked so the nav never depends on the HmRag
             server, and so it can take currentColor. -->
        <svg width="64" height="19" viewBox="0 0 74 22" fill="none" aria-hidden="true">
          <path
            d="M1 13h13.5l4-9 6.5 17 5.5-12 3.5 4H73"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="pulse-word">PULSE</span>
      </span>
      <!-- The letters that spell PULSE are emphasised. They have to be: the line is uppercased,
           so nothing else distinguishes an initial from any other letter. -->
      <span class="pulse-tagline">Healthmark
        <b>P</b>roduct <b>U</b>nderstanding &amp; <b>L</b>ive <b>S</b>upport <b>E</b>ngine</span>
    </div>

    <v-tabs grow color="blue-grey lighten-5" slider-color="primary" class="pulse-tabs">
      <v-tab v-if="userCanSeePulseChat" :to="{ name: 'item-search' }" exact>
        <v-icon class="mr-2">question_answer</v-icon> Chat
      </v-tab>
      <v-tab v-if="userCanSeePulseWiki" :to="{ name: 'item-wiki' }" exact>
        <v-icon class="mr-2">library_books</v-icon> Wiki
      </v-tab>
      <v-tab :to="{ name: 'item-product-search' }" exact>
        <v-icon class="mr-2">search</v-icon> Search
      </v-tab>
    </v-tabs>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  computed: {
    ...mapGetters('auth', [
      'userCanSeePulseChat',
      'userCanSeePulseTabs',
      'userCanSeePulseWiki'
    ])
  }
}
</script>

<style scoped>
/* The enclosing v-container adds 24px of padding on every side. Cancel it at the top so the
   brand row sits directly under the green menu instead of floating below it. */
.pulse-nav { margin-bottom: 24px; margin-top: -24px; }

/* Nothing here is clickable, so nothing here may look clickable. */
.pulse-brandrow {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #c9d0d8;
  cursor: default;
  display: flex;
  gap: 16px;
  min-height: 40px;
  padding: 0 8px;
  user-select: none;
}

.pulse-lockup { align-items: center; color: #34abe5; display: inline-flex; flex: none; gap: 8px; }

/* The wordmark takes the logo's ink, not the brand blue: the active tab is #0D47A1, and two
   dark blues in one area — one meaning "active control", one meaning "brand" — is a false signal.
   text-indent cancels the trailing letter-space so the lockup is optically centered. */
.pulse-word { color: #231f20; font: 700 18px/1 system-ui, -apple-system, sans-serif; letter-spacing: .2em; text-indent: .2em; }

/* Same family as the wordmark, set the way HmRag sets it. Only the size is adapted:
   HmRag's .61rem suits a 260px sidebar, not a 40px row. */
.pulse-tagline { color: #6b7783; font: 400 10.5px/1.4 system-ui, -apple-system, sans-serif; letter-spacing: .13em; text-transform: uppercase; }
/* Weight alone is too fine a difference at 10.5px, so the acronym letters take the logo's ink
   as well. Together they read; either on its own does not. */
.pulse-tagline b { color: #231f20; font-weight: 700; }

/* Tabs stretch across the full line, and their labels drop below Vuetify's 14px default so the
   18px wordmark above them clearly outranks them. The brand sits on its own row, so it no longer
   competes with the tabs for the same reading. */
.pulse-tabs >>> .v-tabs__div { font-size: 12px; }
.pulse-tabs >>> .v-tabs__item .v-icon { font-size: 18px; }

/* The tagline is hidden rather than truncated — a description cut mid-word reads as a bug. */
@media (max-width: 899px) {
  .pulse-tagline { display: none; }
}
</style>
